/**
 * PaymentHistory Component
 * Displays list of past payments/invoices from Stripe
 *
 * WP-BUG-BILLING-SYNC-2: Updated to use InvoiceStatus from Stripe API
 */
import { useState } from 'react';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Spinner from '@/components/ui/Spinner';
import { usePaymentHistory } from '@/hooks/useBilling';
import { formatDate, formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';
import type { Payment, InvoiceStatus } from '@/types/billing';

export interface PaymentHistoryProps {
  className?: string;
}

/**
 * Status badge configuration for Stripe invoice statuses
 * WP-BUG-BILLING-SYNC-2: Maps Stripe invoice.status values
 */
const statusConfig: Record<
  InvoiceStatus,
  { bg: string; text: string; label: string }
> = {
  paid: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    label: 'Paid',
  },
  open: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    label: 'Open',
  },
  void: {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    label: 'Void',
  },
  draft: {
    bg: 'bg-gray-100',
    text: 'text-gray-500',
    label: 'Draft',
  },
  uncollectible: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    label: 'Uncollectible',
  },
  unknown: {
    bg: 'bg-gray-100',
    text: 'text-gray-500',
    label: 'Unknown',
  },
};

/**
 * Format billing period as description
 * WP-BUG-BILLING-SYNC-2: Derive description from period dates
 */
function formatBillingPeriod(periodStart: string | null, periodEnd: string | null): string {
  if (periodStart && periodEnd) {
    return `${formatDate(periodStart)} - ${formatDate(periodEnd)}`;
  }
  return 'Subscription payment';
}

/**
 * Single payment row
 * WP-BUG-BILLING-SYNC-2: Updated to use InvoiceHistoryItem schema
 */
function PaymentRow({ payment }: { payment: Payment }) {
  const config = statusConfig[payment.status] || statusConfig.unknown;

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">
          {formatBillingPeriod(payment.periodStart, payment.periodEnd)}
        </div>
        <div className="text-sm text-gray-500">
          {formatDate(payment.createdAt)}
        </div>
      </div>

      <div className="flex items-center gap-4 ml-4">
        <span
          className={cn(
            'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
            config.bg,
            config.text
          )}
        >
          {config.label}
        </span>

        <div className="text-right">
          <div className="text-sm font-medium text-gray-900">
            {formatCurrency(payment.amount / 100, payment.currency)}
          </div>
        </div>

        <div className="flex gap-2">
          {payment.invoicePdf && (
            <a
              href={payment.invoicePdf}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              aria-label={`Download invoice for payment on ${formatDate(payment.createdAt)}`}
            >
              Invoice
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PaymentHistory({ className }: PaymentHistoryProps) {
  const [cursor, setCursor] = useState<string | undefined>();
  const { data, isLoading, error, isFetching } = usePaymentHistory(cursor);

  const handleLoadMore = () => {
    if (data?.nextCursor) {
      setCursor(data.nextCursor);
    }
  };

  return (
    <Card className={cn(className)}>
      <CardHeader
        title="Payment History"
        description="Your past payments and invoices"
      />

      {/* Loading state */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="text-center py-8">
          <p className="text-red-600">Failed to load payment history.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCursor(undefined)}
            className="mt-2"
          >
            Try Again
          </Button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && data?.payments.length === 0 && (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <p className="mt-2 text-gray-500">No payment history yet.</p>
        </div>
      )}

      {/* Payment list */}
      {!isLoading && !error && data && data.payments.length > 0 && (
        <>
          <div className="divide-y divide-gray-100">
            {data.payments.map((payment) => (
              <PaymentRow key={payment.id} payment={payment} />
            ))}
          </div>

          {/* Load more button */}
          {data.hasMore && (
            <div className="mt-4 text-center">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadMore}
                isLoading={isFetching}
              >
                Load More
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
